import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DatasetQuestionRow } from "./DatasetQuestionRow";
import type { DatasetQuestionsSearchResponse } from "@/models/Dataset";

interface QuestionsListProps {
  questions: DatasetQuestionsSearchResponse[];
  selectedQuestionId: string;
  onQuestionSelect: (question: DatasetQuestionsSearchResponse) => void;
  searchTerm: string;
  isFetching: boolean;
  hasMore: boolean;
  observerTarget: React.RefObject<HTMLDivElement | null> | ((node: HTMLDivElement | null) => void);
  questionRowClassName?: string;
  compact?: boolean;
  recentQuestionIds?: string[];
  /** Hides the infinite-scroll footer (spinner / "All questions loaded") —
   *  used when the list is collapsed to a single selected question. */
  hideLoadMore?: boolean;
}

export const QuestionsList: React.FC<QuestionsListProps> = ({
  questions,
  selectedQuestionId,
  onQuestionSelect,
  searchTerm,
  isFetching,
  hasMore,
  observerTarget,
  questionRowClassName,
  compact = false,
  recentQuestionIds = [],
  hideLoadMore = false,
}) => {
  const recentSet = new Set(recentQuestionIds);
  return (
    <Box
      sx={{
        maxHeight: "300px",
        overflow: "auto",
        border: "1px solid var(--color-hp-border-default)",
        borderRadius: "8px",
        userSelect: "none",
      }}
    >
      {questions.map((question, index) => (
        <DatasetQuestionRow
          key={`${question.datasetElementId}-${index}`}
          question={question}
          onSelect={onQuestionSelect}
          isSelected={selectedQuestionId === question.datasetElementId}
          className={questionRowClassName}
          compact={compact}
          isRecentlyUsed={recentSet.has(question.datasetElementId)}
        />
      ))}

      {questions.length === 0 && searchTerm.trim() && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-hp-text-day-secondary, #666666)",
            }}
          >
            No compatible questions found
          </Typography>
        </Box>
      )}

      {questions.length === 0 && !searchTerm.trim() && !isFetching && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-hp-text-day-secondary, #666666)",
            }}
          >
            No questions available
          </Typography>
        </Box>
      )}

      {/* Infinite scroll trigger */}
      {!hideLoadMore && (
        <Box
          ref={observerTarget}
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40px",
          }}
        >
          {isFetching && <CircularProgress size={24} />}
          {!isFetching && !hasMore && questions.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "var(--color-hp-text-day-secondary, #666666)",
              }}
            >
              All questions loaded
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
